import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ProductCard } from "@/components/ProductCard";
import { ArrowRight, Zap, Shield, Truck } from "lucide-react";

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [allRes, newRes] = await Promise.all([
          axios.get(`${API}/products?is_featured=true`),
          axios.get(`${API}/products?is_new=true`)
        ]);
        setProducts(allRes.data.slice(0, 4));
        setNewArrivals(newRes.data.slice(0, 4));
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section pt-20" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=1920&q=80"
            alt="Street racing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#030303]/60 via-[#030303]/40 to-[#030303]"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <span className="label-text text-[#39FF14] text-sm tracking-[0.3em] animate-fade-in">
              STREET RACING CULTURE
            </span>
            <h1 className="mt-4 text-5xl sm:text-6xl lg:text-7xl font-bold heading text-[#EDEDED] leading-[0.9] animate-fade-in" style={{ animationDelay: "0.1s" }}>
              GRIP CITY<br />
              <span className="text-[#39FF14] neon-text">STREET RACING</span>
            </h1>
            <p className="mt-6 text-lg text-[#888] max-w-lg animate-fade-in" style={{ animationDelay: "0.2s" }}>
              Premium apparel and accessories for those who live life at full throttle. 
              Born from the underground, built for the streets.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 animate-fade-in" style={{ animationDelay: "0.3s" }}>
              <Link
                to="/shop"
                className="btn-skew bg-[#39FF14] text-black px-8 py-4 font-bold uppercase tracking-wider animate-pulse-neon"
                data-testid="shop-now-btn"
              >
                <span>Shop Now</span>
              </Link>
              <Link
                to="/suggestions"
                className="btn-skew border border-[#333] text-[#EDEDED] px-8 py-4 font-bold uppercase tracking-wider hover:border-[#39FF14] hover:text-[#39FF14]"
                data-testid="garage-btn"
              >
                <span>The Garage</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Scrolling banner */}
        <div className="absolute bottom-0 left-0 right-0 py-4 bg-[#39FF14] overflow-hidden">
          <div className="animate-marquee whitespace-nowrap flex">
            {[...Array(10)].map((_, i) => (
              <span key={i} className="mx-8 text-black font-bold heading text-lg">
                NEW DROPS WEEKLY • FREE SHIPPING OVER $100 • EXCLUSIVE DESIGNS • 
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" data-testid="categories-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="label-text text-[#39FF14] text-sm tracking-[0.3em]">BROWSE</span>
              <h2 className="mt-2 text-3xl sm:text-4xl heading text-[#EDEDED]">Categories</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/shop/clothing" className="category-card group" data-testid="category-clothing">
              <img
                src="https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=600"
                alt="Clothing"
              />
              <div className="overlay">
                <div>
                  <h3 className="text-2xl heading text-[#EDEDED] group-hover:text-[#39FF14] transition-colors">
                    Clothing
                  </h3>
                  <p className="text-[#888] mt-1">Hoodies • Tees • Jackets • Pants</p>
                </div>
              </div>
            </Link>

            <Link to="/shop/accessories" className="category-card group" data-testid="category-accessories">
              <img
                src="https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=600"
                alt="Accessories"
              />
              <div className="overlay">
                <div>
                  <h3 className="text-2xl heading text-[#EDEDED] group-hover:text-[#39FF14] transition-colors">
                    Accessories
                  </h3>
                  <p className="text-[#888] mt-1">Hats • Bags • Gloves • Keychains</p>
                </div>
              </div>
            </Link>

            <Link to="/shop/apparel" className="category-card group" data-testid="category-apparel">
              <img
                src="https://images.unsplash.com/photo-1576871337622-98d48d1cf531?w=600"
                alt="Apparel"
              />
              <div className="overlay">
                <div>
                  <h3 className="text-2xl heading text-[#EDEDED] group-hover:text-[#39FF14] transition-colors">
                    Apparel
                  </h3>
                  <p className="text-[#888] mt-1">Headwear • Shirts • Shorts • Socks</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* New Arrivals Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0A0A0A]" data-testid="new-arrivals-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="label-text text-[#FF00FF] text-sm tracking-[0.3em]">JUST DROPPED</span>
              <h2 className="mt-2 text-3xl sm:text-4xl heading text-[#EDEDED]">New Arrivals</h2>
            </div>
            <Link
              to="/shop"
              className="flex items-center gap-2 text-[#888] hover:text-[#39FF14] transition-colors"
            >
              <span className="label-text">View All</span>
              <ArrowRight size={20} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-[4/5] skeleton rounded-lg"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
              {newArrivals.map((product) => (
                <div key={product.id} className="animate-fade-in opacity-0">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" data-testid="featured-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="label-text text-[#00FFFF] text-sm tracking-[0.3em]">TOP PICKS</span>
              <h2 className="mt-2 text-3xl sm:text-4xl heading text-[#EDEDED]">Featured</h2>
            </div>
            <Link
              to="/shop"
              className="flex items-center gap-2 text-[#888] hover:text-[#39FF14] transition-colors"
            >
              <span className="label-text">View All</span>
              <ArrowRight size={20} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-[4/5] skeleton rounded-lg"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
              {products.map((product) => (
                <div key={product.id} className="animate-fade-in opacity-0">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0A0A0A]" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-start gap-4 p-6 border border-[#222] hover:border-[#39FF14]/50 transition-colors">
              <div className="p-3 bg-[#39FF14]/10 text-[#39FF14]">
                <Truck size={28} />
              </div>
              <div>
                <h3 className="text-lg heading text-[#EDEDED]">Fast Shipping</h3>
                <p className="mt-1 text-[#888]">Free shipping on orders over $100. Express delivery available.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 border border-[#222] hover:border-[#FF00FF]/50 transition-colors">
              <div className="p-3 bg-[#FF00FF]/10 text-[#FF00FF]">
                <Shield size={28} />
              </div>
              <div>
                <h3 className="text-lg heading text-[#EDEDED]">Quality Guaranteed</h3>
                <p className="mt-1 text-[#888]">Premium materials. Built to last through every ride.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 border border-[#222] hover:border-[#00FFFF]/50 transition-colors">
              <div className="p-3 bg-[#00FFFF]/10 text-[#00FFFF]">
                <Zap size={28} />
              </div>
              <div>
                <h3 className="text-lg heading text-[#EDEDED]">Exclusive Drops</h3>
                <p className="mt-1 text-[#888]">Limited edition releases. Once they're gone, they're gone.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1920&q=80"
            alt="Street racing"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#030303] via-[#030303]/80 to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="max-w-xl">
            <span className="label-text text-[#39FF14] text-sm tracking-[0.3em]">JOIN THE CREW</span>
            <h2 className="mt-4 text-4xl sm:text-5xl heading text-[#EDEDED]">
              Ready to<br />
              <span className="text-[#FF00FF]">Hit the Streets?</span>
            </h2>
            <p className="mt-6 text-lg text-[#888]">
              Get first access to new drops, exclusive deals, and behind-the-scenes content.
            </p>
            <div className="mt-8 flex gap-4">
              <input
                type="email"
                placeholder="Enter your email"
                className="gcsr-input flex-1 max-w-xs"
              />
              <button className="btn-skew bg-[#39FF14] text-black px-6 py-3 font-bold uppercase">
                <span>Subscribe</span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
